/**
 * The arithmetic behind a send that outlives the screen it was started on.
 *
 * Everything here is a decision about bytes and time — which slice goes next,
 * how full the bar is, how long to wait before trying again — kept apart from
 * the thing that actually does it (chat/outbox.ts) so that the awkward cases
 * can be tested without a network, a database or a phone.
 */

/**
 * How much of a file goes in one request.
 *
 * The trade is between requests and re-sending. Every chunk that completes is a
 * chunk that never has to go up again, so smaller is safer on the connection
 * this exists for — a phone on mobile data, in a lift, in a train. But every
 * chunk is also a round trip with the latency to match, and at 256 КБ a 50 МБ
 * video is two hundred of them.
 *
 * Two megabytes is roughly one second of a good mobile uplink and under ten of
 * a bad one, which is the number that matters: it is how much work is thrown
 * away when the connection drops at the worst possible moment.
 */
export const UPLOAD_CHUNK_BYTES = 2 * 1024 * 1024;

/** One file of a send, as far as the progress maths is concerned. */
export interface PartProgress {
  /** The size of the whole part. */
  size: number;
  /** How many of its bytes the server has confirmed. */
  offset: number;
}

/** The half-open range of the next slice to send, or null when there is none. */
export interface ChunkRange {
  start: number;
  end: number;
}

/**
 * The next slice to send for a part that is `offset` bytes in.
 *
 * `null` means the part is complete — including the case of an empty file,
 * which is a real thing to send and would otherwise be a chunk of zero bytes
 * looping forever.
 */
export function chunkFrom(
  offset: number,
  size: number,
  chunkBytes: number = UPLOAD_CHUNK_BYTES,
): ChunkRange | null {
  if (offset >= size) return null;
  return { start: offset, end: Math.min(offset + chunkBytes, size) };
}

/**
 * How far along a send is, from 0 to 1 — counted in bytes across every part
 * rather than in parts.
 *
 * The same reasoning as compressAlbumVideos: an album of nine photos and one
 * video is not 90% done when the photos are up, and a bar that said so would
 * sit at 90% for a minute. A send of nothing at all is complete, which keeps
 * the caller from having to special-case a division by zero.
 */
export function sendProgress(parts: readonly PartProgress[]): number {
  const total = parts.reduce((sum, part) => sum + part.size, 0);
  if (total === 0) return 1;
  const done = parts.reduce((sum, part) => sum + Math.min(part.offset, part.size), 0);
  return Math.min(1, Math.max(0, done / total));
}

/** The first part with bytes still to send, by index. -1 when all are done. */
export function nextPartIndex(parts: readonly PartProgress[]): number {
  return parts.findIndex((part) => part.offset < part.size);
}

/** How long before an interrupted send is tried again. */
const RETRY_BASE_MS = 2_000;
const RETRY_CAP_MS = 60_000;

/**
 * Exponential backoff with a ceiling, from the number of failures so far.
 *
 * Capped at a minute because the thing being waited for is usually a phone
 * getting its signal back, and past a minute the wait stops being politeness
 * to the server and becomes an upload that does not resume when the train
 * leaves the tunnel. The first retry is quick for the same reason: the common
 * failure here is a few seconds long.
 */
export function retryDelayMs(attempts: number): number {
  if (attempts <= 0) return 0;
  return Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** (attempts - 1));
}

/** What the server said about a session this device thinks it owns. */
export interface RemoteUpload {
  offset: number;
  consumed: boolean;
}

export type ResumeDecision =
  /** Carry on from this offset — usually the server's, which is authoritative. */
  | { action: 'continue'; offset: number }
  /** The bytes are already a message; go straight to asking for it. */
  | { action: 'finalize' }
  /** Nothing usable on the other side; open a new session and send it all again. */
  | { action: 'restart' };

/**
 * What to do with a part whose session was opened before the app was last
 * closed — the question every resumed upload starts with.
 *
 * The server's offset wins whenever the two disagree, in both directions, and
 * the reason it can be lower is the ordinary one: a chunk this device counted
 * as sent had its response lost, or landed after the row was read. Trusting the
 * local number there would leave a hole in the middle of the file that nothing
 * would ever notice — the upload would complete, the size would be right, and
 * the video would not play.
 *
 * A server offset *ahead* of the local one is the same event seen from the
 * other side and is simply accepted; those bytes are on disk.
 */
export function resumeFrom(remote: RemoteUpload | null, size: number): ResumeDecision {
  if (remote === null) return { action: 'restart' };
  if (remote.consumed) return { action: 'finalize' };
  if (remote.offset > size) return { action: 'restart' };
  return { action: 'continue', offset: remote.offset };
}
